/**
 * ── Which hostnames ARE the shop — ONE list, read by the Worker and by the shop bundle ─────────
 *
 * Before this file, "the shop owns this whole host" was `hostname.startsWith("shop.")`, typed out
 * in six places (index.ts twice, shop-mail.ts, shop-seo.ts twice, the client router). The real
 * address the shop is moving to on 1 Nov 2026 is Shopify's primary domain, www.zsmerch.co.za —
 * the one Google has indexed — so every one of those tests would have answered "no" there and
 * served the /shop-mounted, noindex copy on the live domain.
 *
 * - `shop.*` keeps working exactly as before (any `shop.` hostname is a shop host).
 * - `www.zsmerch.co.za` is the CANONICAL shop host: the only zsmerch host that is ever indexed.
 * - the bare `zsmerch.co.za` is a shop host that serves nothing itself: it 301s to www, path and
 *   query kept (`shopApexRedirect`), before any other handling.
 *
 * ⚠️ No DOM, no Worker types, no imports — the Vite bundle (`web/src/store/route.tsx`) imports
 * this file directly, like shop-routes.ts. A hostname list in an env var was considered and left
 * out on purpose: the browser half cannot read Worker env, and two lists that can disagree is the
 * bug this file removes. Adding a host is a one-line change here.
 */

/** The shop's canonical public hostname (Shopify's primary domain; Google has it indexed). */
export const SHOP_CANONICAL_HOST = "www.zsmerch.co.za";

/** Shop hostnames that only redirect, to the canonical host. */
export const SHOP_REDIRECT_HOSTS: Readonly<Record<string, string>> = {
  "zsmerch.co.za": SHOP_CANONICAL_HOST,
};

/** Every exact hostname that is the shop (besides the `shop.*` prefix rule). */
export const SHOP_HOSTS: readonly string[] = [SHOP_CANONICAL_HOST, ...Object.keys(SHOP_REDIRECT_HOSTS)];

const norm = (h: string): string => h.trim().toLowerCase().replace(/\.$/, "");

/** True when the shop owns this WHOLE host (its paths are root-relative, no `/shop` mount). */
export function isShopHost(hostname: string): boolean {
  const h = norm(hostname);
  return h.startsWith("shop.") || SHOP_HOSTS.includes(h);
}

/**
 * Indexable by hostname alone: `shop.*` (unchanged behaviour) and the canonical host. The apex
 * never serves a page (it redirects), so it is not indexable either.
 */
export function isIndexableShopHostname(hostname: string): boolean {
  const h = norm(hostname);
  return h.startsWith("shop.") || h === SHOP_CANONICAL_HOST;
}

/**
 * The 301 target for a redirect-only shop host (apex → www), keeping path and query, or for any
 * shop host reached over plain http (→ the same host on https); null otherwise. Always https:
 * the canonical origin is https. Search Console (28 Oct 2026) flagged http://www pages as
 * duplicates — they answered 200 with an http canonical, so every page existed twice.
 */
export function shopApexRedirect(url: URL): string | null {
  const h = norm(url.hostname);
  if (Object.hasOwn(SHOP_REDIRECT_HOSTS, h)) return `https://${SHOP_REDIRECT_HOSTS[h]}${url.pathname}${url.search}`;
  if (url.protocol === "http:" && isShopHost(h)) return `https://${h}${url.pathname}${url.search}`;
  return null;
}
